import { Suspense, useMemo, useCallback, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import type { SidewallModelProps } from "../types";
import type { SidewallTreeRow } from "../../../models";
import {
  HOVER_EMISSIVE_INTENSITY,
  SELECTION_EMISSIVE_INTENSITY,
  EMPTY_POSITION_COLOR,
  MODEL_POSITION,
} from "../constants";
import {
  Viewer3DMaterials,
  Viewer3DEmissiveColors,
  Viewer3DGeometry,
  Viewer3DSettings,
} from "../../../../../shared/theme";
import { getComponentColor } from "../../../theme/colors";
import {
  BURNER_POSITIONS,
  SPECIAL_POSITION_IDS,
  isUpperShellChild,
  isLowerShellChild,
} from "../../../models/constants/parts";

// Gray color for empty positions (no part installed)
const EMPTY_COLOR = new THREE.Color(EMPTY_POSITION_COLOR);

// Burner sub-component positions that enable the burner host to be painted
// Excludes Water Jacket (position 3) which renders independently
const BURNER_HOST_ENABLING_POSITIONS = [1, 2, 4] as const; // Block, Front Face, Nozzle

// Shell position IDs array for membership checks (derived from SPECIAL_POSITION_IDS)
const SHELL_POSITION_IDS = [
  SPECIAL_POSITION_IDS.LOWER_SHELL,
  SPECIAL_POSITION_IDS.UPPER_SHELL,
] as const;

// Path to the GLB model
const SIDEWALL_MODEL_PATH = new URL(
  "../assets/SidewallAssembly.glb",
  import.meta.url,
).href;

// Scale factor for the GLB model (from shared constants)
const MODEL_SCALE = Viewer3DGeometry.MODEL_SCALE;

// Node mapping structure for GLB nodes
// subPositionId: undefined = parent/panel, 3 = water_jacket sub-component
interface NodeMapping {
  positionId: number;
  subPositionId?: number; // For burner sub-components (3 = water_jacket)
}

// Mapping from GLB node names to position IDs
// Based on actual GLB inspection of SidewallAssembly.glb
const GLB_NODE_TO_POSITION: Record<string, NodeMapping> = {
  // Sidewall panels (positions without burners)
  Solid1_315: { positionId: 1 },
  Solid1_316: { positionId: 1 },
  Solid1_317: { positionId: 1 },
  Solid1_318: { positionId: 1 },
  Solid1_319: { positionId: 1 },
  Solid1_320: { positionId: 1 },
  Solid1_321: { positionId: 1 },
  Solid1_324: { positionId: 1 },
  Solid1_325: { positionId: 1 },
  Solid2_4: { positionId: 1 },
  Solid2_5: { positionId: 1 },
  Solid1_303: { positionId: 2 },
  Solid1_304: { positionId: 2 },
  Solid1_305: { positionId: 2 },
  Solid1_306: { positionId: 2 },
  Solid1_307: { positionId: 2 },
  Solid1_308: { positionId: 2 },
  Solid1_309: { positionId: 2 },
  Solid1_310: { positionId: 2 },
  Solid1_311: { positionId: 2 },
  Solid1_312: { positionId: 2 },
  Solid1_270: { positionId: 4 },
  Solid1_271: { positionId: 4 },
  Solid1_272: { positionId: 4 },
  Solid1_273: { positionId: 4 },
  Solid1_274: { positionId: 4 },
  Solid1_275: { positionId: 4 },
  Solid1_276: { positionId: 4 },
  Solid1_277: { positionId: 4 },
  Solid1_278: { positionId: 4 },
  Solid1_279: { positionId: 4 },
  Solid1_182: { positionId: 6 },
  Solid1_183: { positionId: 6 },
  Solid1_184: { positionId: 6 },
  Solid1_185: { positionId: 6 },
  Solid1_186: { positionId: 6 },
  Solid1_187: { positionId: 6 },
  Solid1_188: { positionId: 6 },
  Solid1_189: { positionId: 6 },
  Solid1_192: { positionId: 6 },
  Solid1_193: { positionId: 6 },
  Solid1_169: { positionId: 7 },
  Solid1_170: { positionId: 7 },
  Solid1_171: { positionId: 7 },
  Solid1_172: { positionId: 7 },
  Solid1_173: { positionId: 7 },
  Solid1_174: { positionId: 7 },
  Solid1_177: { positionId: 7 },
  Solid1_178: { positionId: 7 },
  Solid1_179: { positionId: 7 },
  Solid1_180: { positionId: 7 },
  Solid1_195: { positionId: 8 },
  Solid1_196: { positionId: 8 },
  Solid1_197: { positionId: 8 },
  Solid1_198: { positionId: 8 },
  Solid1_199: { positionId: 8 },
  Solid1_200: { positionId: 8 },
  Solid1_201: { positionId: 8 },
  Solid1_202: { positionId: 8 },
  Solid1_205: { positionId: 8 },
  Solid1_206: { positionId: 8 },
  Solid1_225: { positionId: 10 },
  Solid1_226: { positionId: 10 },
  Solid1_227: { positionId: 10 },
  Solid1_228: { positionId: 10 },
  Solid1_229: { positionId: 10 },
  Solid1_230: { positionId: 10 },
  Solid1_231: { positionId: 10 },
  Solid1_232: { positionId: 10 },
  Solid1_233: { positionId: 10 },
  Solid1_234: { positionId: 10 },
  Solid1_281: { positionId: 11 },
  Solid1_282: { positionId: 11 },
  Solid1_283: { positionId: 11 },
  Solid1_284: { positionId: 11 },
  Solid1_285: { positionId: 11 },
  Solid1_286: { positionId: 11 },
  Solid1_288: { positionId: 11 },
  Solid1_289: { positionId: 11 },
  Solid1_290: { positionId: 11 },
  Solid1_292: { positionId: 12 },
  Solid1_293: { positionId: 12 },
  Solid1_294: { positionId: 12 },
  Solid1_295: { positionId: 12 },
  Solid1_296: { positionId: 12 },
  Solid1_297: { positionId: 12 },
  Solid1_298: { positionId: 12 },
  Solid1_299: { positionId: 12 },
  Solid1_300: { positionId: 12 },
  Solid1_301: { positionId: 12 },

  // Burner #1 (position 3)
  Solid1_253: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_254: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_255: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_256: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_257: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_258: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_259: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_260: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_261: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_262: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_263: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_264: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_265: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_266: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_267: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_268: { positionId: 3, subPositionId: 3 }, // Water Jacket
  Solid1_397: { positionId: 3 }, // Burner (Host Block, Front Face, Nozzle) → selects parent

  // Burner #2 (position 5)
  Solid1_208: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_209: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_210: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_211: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_212: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_213: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_216: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_217: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_218: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_219: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_220: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_221: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_222: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_223: { positionId: 5, subPositionId: 3 }, // Water Jacket
  Solid1_396: { positionId: 5 }, // Burner → selects parent

  // Burner #3 (position 9)
  Solid1_236: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_237: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_238: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_239: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_240: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_241: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_244: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_245: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_246: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_247: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_248: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_249: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_250: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_251: { positionId: 9, subPositionId: 3 }, // Water Jacket
  Solid1_395: { positionId: 9 }, // Burner → selects parent

  // Left Sump (position 13)
  Solid1_366: { positionId: 13 },
  Solid1_367: { positionId: 13 },
  Solid1_368: { positionId: 13 },
  Solid1_369: { positionId: 13 },
  Solid1_370: { positionId: 13 },
  Solid1_371: { positionId: 13 },
  Solid1_372: { positionId: 13 },
  Solid1_373: { positionId: 13 },
  Solid1_374: { positionId: 13 },
  Solid2_6: { positionId: 13 },

  // Center Sump (position 14)
  Solid1_384: { positionId: 14 },
  Solid1_385: { positionId: 14 },
  Solid1_386: { positionId: 14 },
  Solid1_387: { positionId: 14 },
  Solid1_388: { positionId: 14 },
  Solid1_389: { positionId: 14 },
  Solid1_390: { positionId: 14 },
  Solid1_391: { positionId: 14 },
  Solid1_392: { positionId: 14 },
  Solid1_393: { positionId: 14 },
  Solid1_394: { positionId: 14 },

  // Right Sump (position 15)
  Solid1_375: { positionId: 15 },
  Solid1_376: { positionId: 15 },
  Solid1_377: { positionId: 15 },
  Solid1_378: { positionId: 15 },
  Solid1_379: { positionId: 15 },
  Solid1_380: { positionId: 15 },
  Solid1_381: { positionId: 15 },
  Solid1_382: { positionId: 15 },
  Solid1_383: { positionId: 15 },
  Solid3: { positionId: 15 },

  // Archway (position 16)
  Solid1_365: { positionId: 16 },

  // Slag Door (position 17)
  Solid1_89: { positionId: 17 },
  Solid1_90: { positionId: 17 },
  Solid1_111: { positionId: 17 },
  Solid1_112: { positionId: 17 },
  Solid1_122: { positionId: 17 },

  // Lower Shell (position 18)
  Solid1_1: { positionId: 18 },
  Solid1_2: { positionId: 18 },
  Solid1_3: { positionId: 18 },
  Solid1_4: { positionId: 18 },
  Solid1_5: { positionId: 18 },
  Solid1_7: { positionId: 18 },
  Solid1_8: { positionId: 18 },
  Solid1_9: { positionId: 18 },
  Solid1_10: { positionId: 18 },
  Solid1_13: { positionId: 18 },
  Solid1_14: { positionId: 18 },
  Solid1_15: { positionId: 18 },
  Solid1_18: { positionId: 18 },
  Solid1_19: { positionId: 18 },
  Solid1_20: { positionId: 18 },
  Solid1_23: { positionId: 18 },
  Solid1_24: { positionId: 18 },
  Solid1_25: { positionId: 18 },
  Solid1_28: { positionId: 18 },
  Solid1_29: { positionId: 18 },
  Solid1_30: { positionId: 18 },
  Solid1_33: { positionId: 18 },
  Solid1_34: { positionId: 18 },
  Solid1_35: { positionId: 18 },
  Solid1_38: { positionId: 18 },
  Solid1_39: { positionId: 18 },
  Solid1_40: { positionId: 18 },
  Solid1_41: { positionId: 18 },
  Solid1_42: { positionId: 18 },
  Solid1_43: { positionId: 18 },
  Solid1_44: { positionId: 18 },
  Solid1_45: { positionId: 18 },
  Solid1_46: { positionId: 18 },
  Solid1_47: { positionId: 18 },
  Solid1_48: { positionId: 18 },
  Solid1_49: { positionId: 18 },
  Solid1_50: { positionId: 18 },
  Solid1_55: { positionId: 18 },
  Solid1_56: { positionId: 18 },
  Solid1_57: { positionId: 18 },
  Solid1_60: { positionId: 18 },
  Solid1_61: { positionId: 18 },
  Solid1_62: { positionId: 18 },
  Solid1_63: { positionId: 18 },
  Solid1_64: { positionId: 18 },
  Solid1_65: { positionId: 18 },
  Solid1_66: { positionId: 18 },
  Solid1_67: { positionId: 18 },
  Solid1_70: { positionId: 18 },
  Solid1_71: { positionId: 18 },
  Solid1_72: { positionId: 18 },
  Solid1_73: { positionId: 18 },
  Solid1_74: { positionId: 18 },
  Solid1_75: { positionId: 18 },
  Solid1_78: { positionId: 18 },
  Solid1_79: { positionId: 18 },
  Solid1_80: { positionId: 18 },
  Solid1_81: { positionId: 18 },
  Solid1_82: { positionId: 18 },
  Solid1_86: { positionId: 18 },
  Solid1_87: { positionId: 18 },
  Solid1_88: { positionId: 18 },
  Solid1_91: { positionId: 18 },
  Solid1_92: { positionId: 18 },
  Solid1_93: { positionId: 18 },
  Solid1_94: { positionId: 18 },
  Solid1_95: { positionId: 18 },
  Solid1_96: { positionId: 18 },
  Solid1_97: { positionId: 18 },
  Solid1_98: { positionId: 18 },
  Solid1_99: { positionId: 18 },
  Solid1_100: { positionId: 18 },
  Solid1_101: { positionId: 18 },
  Solid1_102: { positionId: 18 },
  Solid1_103: { positionId: 18 },
  Solid1_104: { positionId: 18 },
  Solid1_105: { positionId: 18 },
  Solid1_106: { positionId: 18 },
  Solid1_107: { positionId: 18 },
  Solid1_108: { positionId: 18 },
  Solid1_109: { positionId: 18 },
  Solid1_113: { positionId: 18 },
  Solid1_114: { positionId: 18 },
  Solid1_115: { positionId: 18 },
  Solid1_116: { positionId: 18 },
  Solid1_118: { positionId: 18 },
  Solid1_119: { positionId: 18 },
  Solid1_120: { positionId: 18 },
  Solid1_121: { positionId: 18 },
  Solid1_123: { positionId: 18 },
  Solid1_124: { positionId: 18 },
  Solid1_125: { positionId: 18 },
  Solid1_126: { positionId: 18 },
  Solid1_127: { positionId: 18 },
  Solid1_128: { positionId: 18 },
  Solid1_129: { positionId: 18 },
  Solid1_130: { positionId: 18 },
  Solid1_131: { positionId: 18 },
  Solid1_132: { positionId: 18 },
  Solid1_133: { positionId: 18 },
  Solid1_134: { positionId: 18 },
  Solid1_135: { positionId: 18 },
  Solid1_136: { positionId: 18 },
  Solid1_137: { positionId: 18 },
  Solid1_138: { positionId: 18 },
  Solid1_139: { positionId: 18 },
  Solid1_140: { positionId: 18 },
  Solid1_141: { positionId: 18 },
  Solid1_142: { positionId: 18 },
  Solid1_143: { positionId: 18 },
  Solid1_152: { positionId: 18 },
  Solid1_153: { positionId: 18 },
  Solid1_154: { positionId: 18 },
  Solid1_155: { positionId: 18 },
  Solid1_156: { positionId: 18 },
  Solid1_157: { positionId: 18 },
  Solid1_158: { positionId: 18 },
  Solid1_159: { positionId: 18 },
  Solid1_160: { positionId: 18 },
  Solid1_161: { positionId: 18 },
  Solid1_162: { positionId: 18 },
  Solid1_163: { positionId: 18 },
  Solid1_164: { positionId: 18 },
  Solid1_165: { positionId: 18 },
  Solid1_166: { positionId: 18 },
  Solid1_167: { positionId: 18 },
  Solid1_144: { positionId: 18 },
  Solid1_145: { positionId: 18 },
  Solid1_146: { positionId: 18 },
  Solid1_147: { positionId: 18 },
  Solid1_148: { positionId: 18 },
  Solid1_149: { positionId: 18 },
  Solid1_150: { positionId: 18 },
  Solid1_151: { positionId: 18 },

  // Lower Shell (position 18) - continued
  Solid1_6: { positionId: 18 },
  Solid1_11: { positionId: 18 },
  Solid1_16: { positionId: 18 },
  Solid1_22: { positionId: 18 },
  Solid1_26: { positionId: 18 },
  Solid1_31: { positionId: 18 },
  Solid1_36: { positionId: 18 },
  Solid1_52: { positionId: 18 },
  Solid1_53: { positionId: 18 },
  Solid1_85: { positionId: 18 },
  Solid1_110: { positionId: 18 },
  Solid1_117: { positionId: 18 },
  Solid1: { positionId: 18 },
  Solid2: { positionId: 18 },
  Solid2_1: { positionId: 18 },
  Solid2_2: { positionId: 18 },
  Solid2_3: { positionId: 18 },

  // Lower Shell (position 18) - continued
  Solid1_12: { positionId: 18 },
  Solid1_17: { positionId: 18 },
  Solid1_21: { positionId: 18 },
  Solid1_27: { positionId: 18 },
  Solid1_32: { positionId: 18 },
  Solid1_37: { positionId: 18 },
  Solid1_51: { positionId: 18 },
  Solid1_54: { positionId: 18 },

  // Upper Shell (position 19)
  Solid1_168: { positionId: 19 },
  Solid1_214: { positionId: 19 },
  Solid1_215: { positionId: 19 },
  Solid1_175: { positionId: 19 },
  Solid1_176: { positionId: 19 },
  Solid1_181: { positionId: 19 },
  Solid1_190: { positionId: 19 },
  Solid1_191: { positionId: 19 },
  Solid1_194: { positionId: 19 },
  Solid1_203: { positionId: 19 },
  Solid1_204: { positionId: 19 },
  Solid1_207: { positionId: 19 },
  Solid1_224: { positionId: 19 },
  Solid1_235: { positionId: 19 },
  Solid1_242: { positionId: 19 },
  Solid1_243: { positionId: 19 },
  Solid1_252: { positionId: 19 },
  Solid1_269: { positionId: 19 },
  Solid1_280: { positionId: 19 },
  Solid1_287: { positionId: 19 },
  Solid1_291: { positionId: 19 },
  Solid1_302: { positionId: 19 },
  Solid1_314: { positionId: 19 },
  Solid1_322: { positionId: 19 },
  Solid1_323: { positionId: 19 },
  Solid1_326: { positionId: 19 },
  Solid1_327: { positionId: 19 },
  Solid1_328: { positionId: 19 },
  Solid1_329: { positionId: 19 },
  Solid1_330: { positionId: 19 },
  Solid1_331: { positionId: 19 },
  Solid1_332: { positionId: 19 },
  Solid1_333: { positionId: 19 },
  Solid1_334: { positionId: 19 },
  Solid1_335: { positionId: 19 },
  Solid1_336: { positionId: 19 },
  Solid1_337: { positionId: 19 },
  Solid1_338: { positionId: 19 },
  Solid1_339: { positionId: 19 },
  Solid1_340: { positionId: 19 },
  Solid1_341: { positionId: 19 },
  Solid1_342: { positionId: 19 },
  Solid1_343: { positionId: 19 },
  Solid1_344: { positionId: 19 },
  Solid1_345: { positionId: 19 },
  Solid1_346: { positionId: 19 },
  Solid1_347: { positionId: 19 },
  Solid1_348: { positionId: 19 },
  Solid1_349: { positionId: 19 },
  Solid1_350: { positionId: 19 },
  Solid1_351: { positionId: 19 },
  Solid1_352: { positionId: 19 },
  Solid1_353: { positionId: 19 },
  Solid1_354: { positionId: 19 },
  Solid1_355: { positionId: 19 },
  Solid1_356: { positionId: 19 },
  Solid1_357: { positionId: 19 },
  Solid1_358: { positionId: 19 },
  Solid1_359: { positionId: 19 },
  Solid1_360: { positionId: 19 },
  Solid1_361: { positionId: 19 },
  Solid1_362: { positionId: 19 },
  Solid1_363: { positionId: 19 },
  Solid1_364: { positionId: 19 },
};

interface SidewallModelInnerProps {
  sidewallData: SidewallModelProps["sidewallData"];
  selectedLocationId: string | null;
  hoveredLocationId: string | null;
  onSelectLocation: (row: SidewallTreeRow | null) => void;
  onHoverLocation: (locationId: string | null) => void;
}

const SidewallModelInner = ({
  sidewallData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: SidewallModelInnerProps) => {
  const { scene } = useGLTF(SIDEWALL_MODEL_PATH);
  const groupRef = useRef<THREE.Group>(null);

  // Check if a position belongs to the active shell
  const isPositionInActiveShell = useCallback(
    (positionId: number): boolean => {
      // If no shellPositionId set, allow all positions (e.g., SVG thumbnail)
      if (sidewallData.shellPositionId === undefined) return true;

      // Check if position belongs to the active shell's children
      if (sidewallData.shellPositionId === SPECIAL_POSITION_IDS.UPPER_SHELL) {
        return isUpperShellChild(positionId);
      }
      if (sidewallData.shellPositionId === SPECIAL_POSITION_IDS.LOWER_SHELL) {
        return isLowerShellChild(positionId);
      }
      return false;
    },
    [sidewallData.shellPositionId],
  );

  // Get locationId from NodeMapping
  const getLocationId = useCallback(
    (mapping: NodeMapping): string | null => {
      // Shells use equipment-style IDs (equipment-{shellId})
      // Only return ID if it's the ACTIVE shell (matching shellPositionId)
      if (
        (SHELL_POSITION_IDS as readonly number[]).includes(mapping.positionId)
      ) {
        // Only allow interaction with the currently selected shell
        // If shellPositionId is not provided, disable shell interaction
        if (
          sidewallData.shellPositionId !== undefined &&
          mapping.positionId === sidewallData.shellPositionId
        ) {
          return `equipment-${sidewallData.sidewallId}`;
        }
        // Other shell or no shellPositionId - return null to prevent interaction
        return null;
      }

      // Filter child positions based on active shell
      // Don't allow hover on children of the inactive shell
      if (!isPositionInActiveShell(mapping.positionId)) {
        return null;
      }

      if (mapping.subPositionId !== undefined) {
        // Sub-component (e.g., Water Jacket): location-{sidewallId}-{positionId}-{subPositionId}
        return `location-${sidewallData.sidewallId}-${mapping.positionId}-${mapping.subPositionId}`;
      }
      // Parent/panel: location-{sidewallId}-{positionId}
      return `location-${sidewallData.sidewallId}-${mapping.positionId}`;
    },
    [
      sidewallData.sidewallId,
      sidewallData.shellPositionId,
      isPositionInActiveShell,
    ],
  );

  // Find location data by NodeMapping
  const getLocationByMapping = useCallback(
    (mapping: NodeMapping): SidewallTreeRow | null => {
      const targetLocationId = getLocationId(mapping);
      if (targetLocationId === null) return null;
      return (
        sidewallData.locations.find((loc) => loc.id === targetLocationId) ??
        null
      );
    },
    [sidewallData.locations, getLocationId],
  );

  // Find NodeMapping from node name (traverses up to find parent with mapping)
  const getMappingFromObject = useCallback(
    (object: THREE.Object3D): NodeMapping | null => {
      let current: THREE.Object3D | null = object;
      while (current) {
        if (current.name && GLB_NODE_TO_POSITION[current.name] !== undefined) {
          return GLB_NODE_TO_POSITION[current.name];
        }
        current = current.parent;
      }
      return null;
    },
    [],
  );

  // Create a deep clone of the scene with interactive materials
  const clonedScene = useMemo(() => {
    const clone = scene.clone(true);

    // Deep clone materials and store original colors
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m) => {
            const cloned = m.clone();
            if (cloned instanceof THREE.MeshStandardMaterial) {
              cloned.userData.originalColor = cloned.color.clone();
            }
            return cloned;
          });
        } else if (child.material) {
          const cloned = child.material.clone();
          if (cloned instanceof THREE.MeshStandardMaterial) {
            cloned.userData.originalColor = cloned.color.clone();
          }
          child.material = cloned;
        }

        // FIX: Correct material for 105_-_front_plate1 node (has wrong FlakedSatinBlue instead of Gold)
        // This is a GLB model issue - the node has a different material than its counterpart
        if (
          child.parent?.name === "105_-_front_plate1" &&
          child.material instanceof THREE.MeshPhysicalMaterial &&
          child.material.name === "FlakedSatinBlue"
        ) {
          // Create a Gold MeshStandardMaterial with exact values from 105_-_front_plate1_2
          const goldMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color(0.0887, 0.1559, 0.1946),
            metalness: 0,
            roughness: 0.425,
            name: "Gold_Fixed",
          });
          goldMaterial.userData.originalColor = goldMaterial.color.clone();
          child.material = goldMaterial;
        }
      }
    });

    return clone;
  }, [scene]);

  // Check if a location has an installed part
  const hasInstalledPart = useCallback(
    (locationId: string): boolean => {
      const location = sidewallData.locations.find(
        (loc) => loc.id === locationId,
      );
      return location?.HasAssignedPart ?? false;
    },
    [sidewallData.locations],
  );

  // Check if a burner host should be painted (any of Block, Front Face, or Nozzle installed)
  const hasBurnerHostPart = useCallback(
    (positionId: number): boolean => {
      // Check if any of the enabling sub-components have a part installed
      return BURNER_HOST_ENABLING_POSITIONS.some((subPos) => {
        const subLocationId = `location-${sidewallData.sidewallId}-${positionId}-${subPos}`;
        return hasInstalledPart(subLocationId);
      });
    },
    [sidewallData.sidewallId, hasInstalledPart],
  );

  // Update materials based on hover/selection state and installation status
  useMemo(() => {
    if (!clonedScene) return;

    // Update all meshes
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        // Find which location this mesh belongs to
        const mapping = getMappingFromObject(child);
        const meshLocationId = mapping ? getLocationId(mapping) : null;

        // Determine selection/hover/installed state
        const isSelected =
          meshLocationId !== null && meshLocationId === selectedLocationId;
        const isHovered =
          meshLocationId !== null && meshLocationId === hoveredLocationId;

        // For burner host nodes (positions 3, 5, 9 without subPositionId),
        // check if any enabling sub-component (Block, Front Face, Nozzle) has a part
        const isBurnerHostNode =
          mapping !== null &&
          (BURNER_POSITIONS as readonly number[]).includes(
            mapping.positionId,
          ) &&
          mapping.subPositionId === undefined;

        const isInstalled = isBurnerHostNode
          ? hasBurnerHostPart(mapping!.positionId)
          : meshLocationId !== null && hasInstalledPart(meshLocationId);

        // Apply color and emissive effect based on state
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        materials.forEach((mat) => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            // Apply color based on installation status
            if (isInstalled && mapping) {
              // Use theme color based on position
              const themeColor = getComponentColor(mapping.positionId);
              mat.color.set(themeColor);
            } else {
              // Use gray color for empty positions
              mat.color.copy(EMPTY_COLOR);
              mat.metalness = Viewer3DMaterials.EMPTY_METALNESS;
              mat.roughness = Viewer3DMaterials.EMPTY_ROUGHNESS;
            }

            // Apply emissive effect based on selection/hover state
            if (isSelected) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = SELECTION_EMISSIVE_INTENSITY;
            } else if (isHovered) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = HOVER_EMISSIVE_INTENSITY;
            } else {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.DEFAULT);
              mat.emissiveIntensity =
                Viewer3DSettings.DEFAULT_EMISSIVE_INTENSITY;
            }
          }
        });
      }
    });
  }, [
    clonedScene,
    selectedLocationId,
    hoveredLocationId,
    getMappingFromObject,
    getLocationId,
    hasInstalledPart,
  ]);

  // Handle click on the model
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();

      const mapping = getMappingFromObject(event.object);
      if (mapping !== null) {
        // Only allow click on valid/selectable parts (belonging to active shell)
        const locationId = getLocationId(mapping);
        if (locationId !== null) {
          const location = getLocationByMapping(mapping);
          onSelectLocation(location);
        }
      }
    },
    [
      getMappingFromObject,
      getLocationId,
      getLocationByMapping,
      onSelectLocation,
    ],
  );

  // Handle pointer over (hover)
  const handlePointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();

      const mapping = getMappingFromObject(event.object);

      if (mapping !== null) {
        const locationId = getLocationId(mapping);
        if (locationId !== null) {
          // Only show pointer cursor for valid/selectable parts
          document.body.style.cursor = "pointer";
          onHoverLocation(locationId);
        }
      }
    },
    [getMappingFromObject, getLocationId, onHoverLocation],
  );

  // Handle pointer out (hover end)
  const handlePointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      document.body.style.cursor = "auto";
      onHoverLocation(null);
    },
    [onHoverLocation],
  );

  return (
    <group ref={groupRef} position={MODEL_POSITION}>
      <primitive
        object={clonedScene}
        scale={MODEL_SCALE}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      />
    </group>
  );
};

export const SidewallModel = ({
  sidewallData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: SidewallModelProps) => {
  return (
    <Suspense
      fallback={
        <mesh>
          <boxGeometry
            args={[
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
            ]}
          />
          <meshStandardMaterial color="red" />
        </mesh>
      }
    >
      <SidewallModelInner
        sidewallData={sidewallData}
        selectedLocationId={selectedLocationId}
        hoveredLocationId={hoveredLocationId}
        onSelectLocation={onSelectLocation}
        onHoverLocation={onHoverLocation}
      />
    </Suspense>
  );
};

// Preload the GLB model
useGLTF.preload(SIDEWALL_MODEL_PATH);
